import { useEffect, useRef, useState } from "react";
import { AlertTriangle, FileQuestion, Loader2 } from "lucide-react";
import { toast } from "@/lib/toast";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useConceptList } from "@/hooks/useConcepts";
import { useRetryEvent } from "@/hooks/useRetryEvent";
import {
  useAddEventToConcept,
  useDismissEvent,
  useMatchEvent,
  useZoteroSyncEvents,
} from "@/hooks/useZoteroSyncEvents";
import apiClient from "@/lib/api-client";
import type { ZoteroSyncEvent } from "@/types/zoteroSync";

interface PaperHit {
  id: string;
  title: string;
  doi?: string | null;
  year?: number | null;
}

function eventTitle(e: ZoteroSyncEvent): string {
  return (
    e.paper_title ?? e.title_hint ?? e.filename_hint ?? "(unknown title)"
  );
}

async function readErrorDetail(err: unknown): Promise<string | null> {
  try {
    const httpErr = err as {
      response?: { json?: () => Promise<{ detail?: string }> };
    };
    if (httpErr?.response?.json) {
      const body = await httpErr.response.json();
      return body?.detail ?? null;
    }
  } catch {
    /* fall through */
  }
  return null;
}

export function UnmatchedView() {
  const { unmatched } = useZoteroSyncEvents();
  const dismiss = useDismissEvent();
  const match = useMatchEvent();
  const addToConcept = useAddEventToConcept();

  const [matchTarget, setMatchTarget] = useState<string | null>(null);
  const [pickedPaperId, setPickedPaperId] = useState<string | null>(null);
  const [showDismissed, setShowDismissed] = useState(false);

  const visible = showDismissed
    ? unmatched
    : unmatched.filter(
        (e) =>
          e.status === "unmatched" ||
          e.status === "skipped_blob_unreachable",
      );

  // Reset transient picker state when the active event flips.
  useEffect(() => {
    setPickedPaperId(null);
  }, [matchTarget]);

  const handlePick = (eventId: string, paperId: string) => {
    if (match.isPending) return; // Hard-stop double-clicks during the network call.
    setPickedPaperId(paperId);
    match.mutate(
      { id: eventId, paper_id: paperId },
      {
        onSuccess: () => {
          toast.success("Matched");
          setMatchTarget(null);
          setPickedPaperId(null);
        },
        onError: async (err) => {
          const detail = await readErrorDetail(err);
          toast.error("Match failed", {
            description: detail ?? undefined,
          });
          setPickedPaperId(null);
        },
      },
    );
  };

  return (
    <div className="min-w-0 space-y-3">
      <div className="flex items-center justify-end">
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={showDismissed}
            onChange={(e) => setShowDismissed(e.target.checked)}
          />
          Show dismissed
        </label>
      </div>

      {visible.length === 0 ? (
        <p className="rounded border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
          No unmatched items. Sync caught everything.
        </p>
      ) : (
        <ul className="space-y-2">
          {visible.map((e) => {
            const isPicking = matchTarget === e.id;
            if (e.status === "skipped_blob_unreachable") {
              return (
                <li
                  key={e.id}
                  className="rounded border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/60 dark:bg-amber-950/30"
                >
                  <StuckBlobRow event={e} />
                </li>
              );
            }
            return (
              <li
                key={e.id}
                className="rounded border border-border bg-card/50 p-3"
              >
                {!isPicking ? (
                  <RowCompact
                    event={e}
                    onMatch={() => setMatchTarget(e.id)}
                    isAdding={
                      addToConcept.isPending &&
                      addToConcept.variables?.id === e.id
                    }
                    onAddToConcept={(conceptId, conceptName) => {
                      addToConcept.mutate(
                        { id: e.id, concept_id: conceptId },
                        {
                          onSuccess: () => {
                            toast.success(`Added to "${conceptName}"`);
                          },
                          onError: async (err) => {
                            const detail = await readErrorDetail(err);
                            toast.error("Add failed", {
                              description: detail ?? undefined,
                            });
                          },
                        },
                      );
                    }}
                    onDismiss={() => {
                      dismiss.mutate(e.id);
                      toast.success("Dismissed");
                    }}
                  />
                ) : (
                  <PaperPicker
                    event={e}
                    matching={match.isPending}
                    pickedPaperId={pickedPaperId}
                    onPick={(paperId) => handlePick(e.id, paperId)}
                    onCancel={() => setMatchTarget(null)}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

interface StuckBlobRowProps {
  event: ZoteroSyncEvent;
}

function StuckBlobRow({ event }: StuckBlobRowProps) {
  const retry = useRetryEvent();
  const onRetry = () => {
    if (retry.isPending) return;
    retry.mutate(event.id, {
      onSuccess: (e) => {
        if (e.status === "skipped_blob_unreachable") {
          toast.info("Zotero is still returning an error", {
            description: e.reason ?? undefined,
          });
        } else {
          toast.success("Retry succeeded");
        }
      },
      onError: async (err) => {
        const detail = await readErrorDetail(err);
        toast.error("Retry failed", {
          description: detail ?? undefined,
        });
      },
    });
  };
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <AlertTriangle className="size-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <span className="min-w-0 truncate font-medium">
            Could not download from Zotero
          </span>
        </div>
        <div className="mt-1 truncate text-xs text-muted-foreground">
          {eventTitle(event)}
        </div>
        {event.reason && (
          <div className="mt-1 break-all text-xs text-muted-foreground">
            {event.reason}
          </div>
        )}
      </div>
      <div className="flex shrink-0 gap-2">
        <Button
          size="xs"
          variant="outline"
          onClick={onRetry}
          disabled={retry.isPending}
        >
          {retry.isPending ? (
            <>
              <Loader2 className="mr-1 size-3 animate-spin" />
              Retrying…
            </>
          ) : (
            "Retry"
          )}
        </Button>
      </div>
    </div>
  );
}

interface RowCompactProps {
  event: ZoteroSyncEvent;
  onMatch: () => void;
  onAddToConcept: (conceptId: string, conceptName: string) => void;
  isAdding: boolean;
  onDismiss: () => void;
}

function RowCompact({
  event,
  onMatch,
  onAddToConcept,
  isAdding,
  onDismiss,
}: RowCompactProps) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <FileQuestion className="size-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 truncate font-medium">
            {eventTitle(event)}
          </span>
          {event.provider && event.provider !== "zotero" && (
            <span
              data-testid="provider-badge"
              className="shrink-0 whitespace-nowrap rounded bg-muted px-1 py-0.5 font-mono text-[10px] uppercase tracking-wide text-muted-foreground"
            >
              {event.provider}
            </span>
          )}
        </div>
        <div className="mt-1 break-all text-xs text-muted-foreground">
          {event.doi && <code className="font-mono">{event.doi}</code>}
          <span className="ml-2 whitespace-nowrap rounded bg-muted px-1 py-0.5 font-mono text-[10px]">
            {event.source}
          </span>
        </div>
      </div>
      <div className="flex shrink-0 gap-2">
        <Button size="xs" variant="ghost" onClick={onMatch}>
          Match…
        </Button>
        <AddToConceptPopover
          isAdding={isAdding}
          onPick={onAddToConcept}
        />
        <Button size="xs" variant="ghost" onClick={onDismiss}>
          Dismiss
        </Button>
      </div>
    </div>
  );
}

interface AddToConceptPopoverProps {
  isAdding: boolean;
  onPick: (conceptId: string, conceptName: string) => void;
}

function AddToConceptPopover({ isAdding, onPick }: AddToConceptPopoverProps) {
  const { data: concepts } = useConceptList();
  const [open, setOpen] = useState(false);

  // Stable alpha sort. Falls back to empty list while loading.
  const sorted = (concepts ?? [])
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="xs" variant="ghost" disabled={isAdding}>
          {isAdding ? (
            <>
              <Loader2 className="mr-1 size-3 animate-spin" />
              Adding…
            </>
          ) : (
            "Add…"
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-1"
        align="end"
        data-testid="add-to-concept-popover"
      >
        <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
          Add to concept
        </div>
        {sorted.length === 0 ? (
          <div className="px-2 py-2 text-xs text-muted-foreground">
            No concepts yet.
          </div>
        ) : (
          <ul className="max-h-60 overflow-y-auto">
            {sorted.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => {
                    onPick(c.id, c.name);
                    setOpen(false);
                  }}
                  className="w-full truncate rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
                >
                  {c.name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}

interface PaperPickerProps {
  event: ZoteroSyncEvent;
  matching: boolean;
  pickedPaperId: string | null;
  onPick: (paperId: string) => void;
  onCancel: () => void;
}

function PaperPicker({
  event,
  matching,
  pickedPaperId,
  onPick,
  onCancel,
}: PaperPickerProps) {
  const titleHint = eventTitle(event);
  const [q, setQ] = useState(titleHint);
  const [hits, setHits] = useState<PaperHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const initialAutoSearch = useRef(false);

  const search = async (query: string) => {
    if (query.trim().length < 3) {
      setHits([]);
      setSearched(true);
      return;
    }
    setSearching(true);
    try {
      const res = await apiClient
        .get(`api/papers/search?q=${encodeURIComponent(query)}&limit=10`)
        .json<PaperHit[]>();
      setHits(res);
    } catch {
      setHits([]);
    } finally {
      setSearching(false);
      setSearched(true);
    }
  };

  // Auto-run the initial search using the title hint so the user lands on
  // results immediately. Skips the redundant "see title → click search →
  // see same title in results" round-trip.
  useEffect(() => {
    if (initialAutoSearch.current) return;
    initialAutoSearch.current = true;
    void search(titleHint);
  }, [titleHint]);

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0 break-all text-xs text-muted-foreground">
          From Zotero{" "}
          {event.doi && (
            <code className="ml-1 font-mono">{event.doi}</code>
          )}
        </div>
        <Button
          size="xs"
          variant="ghost"
          onClick={onCancel}
          disabled={matching}
        >
          Cancel
        </Button>
      </div>

      <div className="flex gap-2">
        <Input
          autoFocus
          value={q}
          onChange={(ev) => setQ(ev.target.value)}
          onKeyDown={(ev) => ev.key === "Enter" && search(q)}
          placeholder="Refine search…"
          className="text-sm"
          disabled={matching}
        />
        <Button
          size="sm"
          variant="secondary"
          onClick={() => search(q)}
          disabled={searching || matching || q.trim().length < 3}
        >
          {searching ? (
            <>
              <Loader2 className="size-3.5 animate-spin" /> Searching…
            </>
          ) : (
            "Search"
          )}
        </Button>
      </div>

      {searching && hits.length === 0 && (
        <div className="flex items-center gap-2 px-2 text-xs text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" />
          Searching your corpus…
        </div>
      )}

      {!searching && searched && hits.length === 0 && (
        <p className="rounded border border-dashed border-border px-3 py-3 text-center text-xs text-muted-foreground">
          No papers in your corpus match this search. Try editing the query
          above, or Dismiss this entry.
        </p>
      )}

      {hits.length > 0 && (
        <ul className="max-h-60 space-y-1 overflow-y-auto">
          {hits.map((p) => {
            const isThisOne = pickedPaperId === p.id;
            const disabled = matching;
            return (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => onPick(p.id)}
                  disabled={disabled}
                  className="flex w-full items-center gap-3 rounded px-2 py-1.5 text-left text-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{p.title}</div>
                    {p.doi && (
                      <div className="break-all text-xs text-muted-foreground">
                        {p.doi} · {p.year ?? "n.d."}
                      </div>
                    )}
                  </div>
                  {isThisOne && matching && (
                    <Loader2 className="size-4 shrink-0 animate-spin text-violet-500" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
